import { useEffect, useRef, useState } from 'react';
import styles from './GoogleButton.module.scss';
import { Button } from 'UI';
import { withStyles } from 'vopay';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

type GoogleCredentialResponse = {
  credential: string;
};

type GoogleAccountsId = {
  initialize: (config: { client_id: string; callback: (response: GoogleCredentialResponse) => void }) => void;
  renderButton: (parent: HTMLElement, options: { theme: 'outline'; size: 'large'; width?: number }) => void;
};

// window.google is declared as `any` by the component library (GoogleAddressField),
// so narrow it through a typed accessor instead of redeclaring the global.
function getGoogleAccountsId(): GoogleAccountsId | undefined {
  const google: { accounts?: { id?: GoogleAccountsId } } | undefined = window.google;

  return google?.accounts?.id;
}

let gsiScriptPromise: Promise<void> | undefined;

function loadGsiScript(): Promise<void> {
  gsiScriptPromise ??= new Promise((resolve, reject) => {
    if (getGoogleAccountsId()) {
      resolve();

      return;
    }

    const script = document.createElement('script');
    script.src = GSI_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services script'));
    document.head.appendChild(script);
  });

  return gsiScriptPromise;
}

function emailFromCredential(credential: string): string {
  try {
    const payloadJson = atob(credential.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload: { email?: unknown } = JSON.parse(payloadJson);

    return typeof payload.email === 'string' ? payload.email : '';
  } catch {
    return '';
  }
}

function GoogleLogo() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />

      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />

      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />

      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

export type GoogleLoginResult = { email: string; credential: string };

type GoogleButtonProps = {
  onLogin: (res: GoogleLoginResult) => void;
  label?: string;
};

export default function GoogleButton({ onLogin, label = 'Continue with Google' }: GoogleButtonProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Login' });
  // Held in a ref so a new onLogin identity doesn't re-run the effect below —
  // re-initializing GIS appends a second hidden button to the overlay.
  const handleCredentialRef = useRef<(credential: string) => void>(() => {});

  useEffect(() => {
    handleCredentialRef.current = (credential: string) => {
      const email = emailFromCredential(credential);

      // A credential we can't read an email out of can't identify a user, and the
      // username keys downstream lookups, so fail visibly instead of logging in as ''.
      if (!email) {
        alertDialog.error(
          t('SSO.errorTitle', { defaultValue: 'Error' }),
          t('SSO.noEmailError', {
            defaultValue: 'Google did not return an email address for this sign-in. Please try again.',
          }),
        );

        return;
      }

      onLogin({ credential, email });
    };
  });

  useEffect(() => {
    if (!clientId) return () => {};

    let cancelled = false;

    loadGsiScript()
      .then(() => {
        const accountsId = getGoogleAccountsId();

        if (cancelled || !overlayRef.current || !accountsId) return;

        accountsId.initialize({
          client_id: clientId,
          callback: ({ credential }) => handleCredentialRef.current(credential),
        });

        accountsId.renderButton(overlayRef.current, { theme: 'outline', size: 'large', width: 400 });
        setReady(true);
      })
      .catch((error) => console.warn(error));

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (!clientId) return null;

  // The design's outline button is the visual layer; the official GIS button
  // (which cannot be restyled) is stacked invisibly on top to receive the click,
  // because only it can start the Google credential flow. It stays mounted (just
  // hidden) until GIS is ready, because renderButton needs the overlay element.
  return (
    <Button id="sso-google" size="large" className={cn('googleButtonWrap', !ready && 'hidden')}>
      <div className={cn('gridItem', 'logo')} aria-hidden="true">
        <GoogleLogo />
      </div>

      <div className={cn('gridItem')} aria-hidden="true">
        {label}
      </div>

      <div ref={overlayRef} className={cn('gsiOverlay')} />
    </Button>
  );
}
